import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import SectionHeader from './ui/SectionHeader';
import Modal from './ui/Modal';

const PhotoGallery = () => {
  const [selectedImg, setSelectedImg] = useState(null);
  const [category, setCategory] = useState('all');

  const photos = useMemo(
    () => [
      {
        id: 'IMG001',
        img: 'https://lh3.googleusercontent.com/pw/AP1GczN0MwAZD_we_WPstsPgRLNq3f1YeEmT16_KNrsvwB_PDlgJORtse6fxKxQM3bFr2F2Ngn-LxXAXvhSIogQf2ODezvbFcLFHlFRs7fdCLNLZZikRKu6d',
        title: 'Safety Talk',
        category: 'training',
        date: '2018-12-27',
        location: 'Janus Dredger Barge',
        description: 'Conducting safety talks about safe mooring works',
      },
      {
        id: 'IMG002',
        img: 'https://lh3.googleusercontent.com/pw/AP1GczNRbuPPRusb2-_KweII54bo0e2VtvacyB9NU7LMb1F3eslb2Qqiuy3koQHdLNvsEu029l296OAoErj-p1JW0nmWAa6763NudkuX9CV8RsM5c8ChQ29S',
        title: 'Night Safety Patrol',
        category: 'site',
        date: '2018-09-24',
        location: 'PLTU Jepara',
        description: 'Doing night patrol at construction site',
      },
      {
        id: 'IMG003',
        img: 'https://lh3.googleusercontent.com/pw/AP1GczP_LRHf9aBL-pL6--I2csv3y-13IzANCEpyUrp31V3LlrlHPfF-5ad67T5gAdJ25hcsh_iNC9eA7s6m5DRWryBaU3e5eUA4AEfTkJNCfH5oXVf74Teh',
        title: 'Fire Extinguisher Training',
        category: 'training',
        date: '2019-02-19',
        location: 'Grab Dredger Minass',
        description: 'Conducting training and fire Drill',
      },
      {
        id: 'IMG004',
        img: 'https://lh3.googleusercontent.com/pw/AP1GczNGJaKAcXp1ibeJOj8bNWCzd3yLMERqPF3V_0rhpZ-5SoJ7wFVJ8ihRbLxq-6V6fMDwHFbQqDrI4XwNnbMyknB0sx3dLDzb6Ii8SPsJOy7szQMnqfwP',
        title: 'Risk Assessment Review',
        category: 'assessment',
        date: '2019-03-01',
        location: 'TB Tolak 12',
        description: 'Review risk assessment share to Crew',
      },
      {
        id: 'IMG005',
        img: 'https://lh3.googleusercontent.com/pw/AP1GczNiLWmCHaEmOCB2FdQvltC1QNsI74cBCbHDYFBT0zejI6Ii-XuABekGxeplaoPcbl8IDzNXQw98ChnbX7FXWjeSJZZznDN6Bxi3-6pRyN7IZaLCbZWx',
        title: 'PTW Training',
        category: 'training',
        date: '2019-05-15',
        location: 'Grab Dredger Ketam',
        description: 'Training Permit to Work at Grab Dredger Ketam',
      },
      {
        id: 'IMG006',
        img: 'https://lh3.googleusercontent.com/pw/AP1GczNhC8IdUp3EPDnAeJfKOuF1mx3db5qPaFajmsU_f1CGacrwIHgJbXe6NTnnO7PmBXKVe-TBvdLo57UerbLZlpXwEuQvDS6gIBPHMGXvFL1lsaItaHuo',
        title: 'HSE Rewards',
        category: 'reward',
        date: '2021-02-13',
        location: 'Patimban Project',
        description: 'Give reward to best safety participant',
      },
    ],
    []
  );

  const categories = [
    { label: 'All', value: 'all' },
    { label: 'Training', value: 'training' },
    { label: 'Assessment', value: 'assessment' },
    { label: 'Site', value: 'site' },
    { label: 'Reward', value: 'reward' },
  ];

  const filteredPhotos = photos.filter((photo) =>
    category === 'all' ? true : photo.category === category
  );

  const handleNext = useCallback(() => {
    const currentIndex = photos.findIndex((photo) => photo.id === selectedImg?.id);
    const nextIndex = (currentIndex + 1) % photos.length;
    setSelectedImg(photos[nextIndex]);
  }, [selectedImg, photos]);

  const handlePrevious = useCallback(() => {
    const currentIndex = photos.findIndex((photo) => photo.id === selectedImg?.id);
    const previousIndex = (currentIndex - 1 + photos.length) % photos.length;
    setSelectedImg(photos[previousIndex]);
  }, [selectedImg, photos]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'ArrowLeft') handlePrevious();
      if (event.key === 'ArrowRight') handleNext();
    },
    [handlePrevious, handleNext]
  );

  useEffect(() => {
    if (!selectedImg) return undefined;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImg, handleKeyDown]);

  return (
    <section id="gallery" className="section-rule bg-btc-surface">
      <div className="container-max section-padding py-24">
        <SectionHeader
          number="08"
          label="Archive"
          title="Gallery"
          kicker="Photos from site work and training."
        />

        <div className="mb-8 flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat.value}
              type="button"
              onClick={() => setCategory(cat.value)}
              className={`min-h-11 rounded-full px-5 py-3 font-mono text-xs font-medium uppercase tracking-widest transition-all duration-200 ${
                category === cat.value
                  ? 'bg-gradient-to-r from-btc-burn to-btc-orange text-btc-void shadow-glow-btn'
                  : 'border border-white/20 text-white hover:border-white hover:bg-white/10'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredPhotos.map((photo) => (
            <button
              key={photo.id}
              type="button"
              onClick={() => setSelectedImg(photo)}
              className="group lift-card overflow-hidden text-left"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={photo.img}
                  alt={photo.title}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="border-t border-white/10 p-4">
                <p className="label">{photo.id}</p>
                <h3 className="mt-2 font-heading font-semibold text-white">{photo.title}</h3>
                <p className="mt-1 font-mono text-xs text-btc-muted">
                  {photo.date} · {photo.location}
                </p>
              </div>
            </button>
          ))}
        </div>

        {filteredPhotos.length === 0 && (
          <div className="surface-card p-16 text-center">
            <p className="font-heading text-2xl font-semibold text-white">No files in category</p>
            <p className="mt-2 text-sm text-btc-muted">Select a different filter.</p>
          </div>
        )}
      </div>

      <Modal
        open={Boolean(selectedImg)}
        onClose={() => setSelectedImg(null)}
        title={selectedImg?.title}
        indexLabel={selectedImg?.id}
        actions={
          <>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handlePrevious();
              }}
              aria-label="Previous image"
              className="icon-btn"
            >
              <ChevronLeft size={18} strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handleNext();
              }}
              aria-label="Next image"
              className="icon-btn"
            >
              <ChevronRight size={18} strokeWidth={2} />
            </button>
          </>
        }
      >
        {selectedImg && (
          <>
            <div className="overflow-hidden rounded-xl border border-white/10 bg-btc-void">
              <img
                src={selectedImg.img}
                alt={selectedImg.title}
                className="max-h-96 w-full object-contain"
              />
            </div>
            <dl className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <dt className="label">Date</dt>
                <dd className="mt-1 font-mono text-sm text-white">{selectedImg.date}</dd>
              </div>
              <div>
                <dt className="label">Location</dt>
                <dd className="mt-1 text-white">{selectedImg.location}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="label">Description</dt>
                <dd className="mt-1 text-btc-muted">{selectedImg.description}</dd>
              </div>
            </dl>
          </>
        )}
      </Modal>
    </section>
  );
};

export default PhotoGallery;
